// © Microsoft Corporation. All rights reserved.

import { ProductTypes, initializeSubscriptionSdk, renderCreateSubscription } from "@msnews/subscription-sdk-wrapper";
import { SubscriptionProductTypes } from "@msnews/core";
import { addOrUpdateTelemetryObject, enums, getExternalURL, placeholderImages, renderHybrid } from "@msnews/social-utils";
import { TelemetryPageType } from "@msnews/telemetry-contracts";
import { AccentButton } from "@microsoft/fast-components-react-msft";
import FocusLock from "react-focus-lock";

import React from "react";
import { SocialProps } from "../../Social.props";
import { classNames } from "@microsoft/fast-web-utilities";
import { getXPayToken } from "@msnews/social-data-service";
import { hasPremiumSubscription } from "../../utils/subscriptionHelpers";
import { gradientOverlayTelemetryContracts, GradientOverlayTelemetryObjects } from "./GradientOverlay.telemetry";

interface GradientOverlayState {
    overlayVisible: boolean;
}

export default class GradientOverlay extends React.Component<SocialProps, GradientOverlayState> {
    private subscriptionChecked: boolean;
    private buttonAnonymouslyClicked: boolean;
    telemetryObjects: GradientOverlayTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            overlayVisible: false
        };
        this.telemetryObjects= null;
    }

    componentDidMount() {
        const { me, gradientOverlayData } = this.props;
        if (gradientOverlayData && gradientOverlayData.subscriptionProductType === SubscriptionProductTypes.Premium) {
            initializeSubscriptionSdk();
            if (!me) {
                this.props.usersConnector.fetchMe();
            } else if (gradientOverlayData) {
                this.checkSubscription();
            }
        }
    }

    componentDidUpdate(prevProps) {
        const { me, gradientOverlayData } = this.props;

        if (!prevProps.gradientOverlayData && this.props.gradientOverlayData && gradientOverlayData.subscriptionProductType === SubscriptionProductTypes.Premium) {
            initializeSubscriptionSdk();
        }

        if (!this.subscriptionChecked && me && gradientOverlayData) {
            this.checkSubscription();
        }

        // Take action if user has logged-in after clicking the subscribe button
        if (prevProps.me?.state == enums.userStateType.anonymous && this.props.me?.state != enums.userStateType.anonymous) {
            if (this.buttonAnonymouslyClicked === true) {
                hasPremiumSubscription(me, gradientOverlayData.publisherProfileId).then(
                    hasSubscription => {
                        if (hasSubscription) {
                            this.deactivateComponent();
                        } else {
                            this.renderCreateSubscriptionPanel();
                        }
                    }
                );
                this.buttonAnonymouslyClicked = undefined;
            }
        }
    }

    async checkSubscription() {
        this.subscriptionChecked = true;
        const { me, gradientOverlayData } = this.props;

        if (gradientOverlayData.subscriptionProductType === SubscriptionProductTypes.Premium) {
            const isAnonymous = me && me.state == enums.userStateType.anonymous;
            if (isAnonymous) {
                this.activateComponent();
            } else {
                if (!gradientOverlayData.publisherProfileId) {
                    return this.activateComponent();
                }

                const hasSubscription = await hasPremiumSubscription(me, gradientOverlayData.publisherProfileId);
                if (!hasSubscription) {
                    this.activateComponent();
                } else {
                    this.sendPageViewTelemetrySignal("article");
                }
            }
        }
    }

    activateComponent() {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;

        this.sendPageViewTelemetrySignal("articlepaywall");
        document.body.style.overflowY = "hidden";
        if (isAnonymous) {
            this.setState(() => ({ overlayVisible: true }));
        } else {
            this.renderCreateSubscriptionPanel();
        }
        this.scrollToTop();
    }

    deactivateComponent() {
        this.setState(() => ({ overlayVisible: false }));
        document.body.style.overflowY = "initial";
        this.sendPageViewTelemetrySignal("article");
    }

    sendPageViewTelemetrySignal(pageType: TelemetryPageType): void {
        const { gradientOverlayData } = this.props;
        if (gradientOverlayData.sendPageViewTelemetrySignal) {
            gradientOverlayData.sendPageViewTelemetrySignal(pageType);
        }
    }

    async onButtonClick(e) {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            this.buttonAnonymouslyClicked = true;
            return this.props.popupConnector.openPopup("multiLoginPage");
        } else {
            this.renderCreateSubscriptionPanel();
        }
    }

    async renderCreateSubscriptionPanel() {
        const { gradientOverlayData, me, market } = this.props;
        await renderCreateSubscription({
            publisherId: gradientOverlayData.publisherProfileId,
            publisherName: gradientOverlayData.publisherName,
            publisherLogoUrl: gradientOverlayData.publisherLogoUrl,
            productType: ProductTypes.Premium,
            userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
            locale: market,
            authToken: await getXPayToken(me.id),
            userProfileImageUrl: me.primaryAvatar || placeholderImages.avatar,
            scenario: "article",
            onSuccess: () => {
                this.deactivateComponent();
            }
        });
        this.setState(() => ({ overlayVisible: false }));

        // Manage/Modify paywall stlyes/behavior
        managePaywall(this);
    }

    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    render() {
        const { t, managedClasses, gradientOverlayData, mainTelemetryObject } = this.props;
        if (!gradientOverlayData || !this.state.overlayVisible) { return null; }

        if (mainTelemetryObject) {
            this.telemetryObjects = {
                readFullContentButton: addOrUpdateTelemetryObject(mainTelemetryObject, gradientOverlayTelemetryContracts.readFullContentButton, { id: gradientOverlayData.publisherProfileId }, this.telemetryObjects && this.telemetryObjects.readFullContentButton)
            };
        }

        return (
            <FocusLock>
                <div className={classNames(managedClasses.gradientOverlayContainer)} onClick={(e) => this.onButtonClick(e)}>
                    <div className={classNames("cwt-overlay")}>
                        <AccentButton
                            onClick={(e) => this.onButtonClick(e)}
                            className={managedClasses.overlayButton}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.readFullContentButton, true)}>
                            { t.OverlayGradientSubscribeText }
                        </AccentButton>
                    </div>
                </div>
            </FocusLock>
        );
    }
}

function managePaywall(gradientOverlayInstance: GradientOverlay) {
    const paywallConainerId = "subscription-sdk-container";
    const paywallContainer = document.getElementById(paywallConainerId);
    const paywallStyleOverrideSheet = document.createElement("style");
    paywallStyleOverrideSheet.innerHTML = `#${paywallConainerId} [class^="backdrop"] { height: calc(100vh - 530px); }`;
    document.body.appendChild(paywallStyleOverrideSheet);
    paywallContainer && trapFocus(paywallContainer);

    window.addEventListener("scroll", (e) => {
        if (!gradientOverlayInstance.state.overlayVisible) { return; }
        const firstFocusableEl = paywallContainer && (paywallContainer.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])')[0] as HTMLElement);
        if (!firstFocusableEl) { return; }

        firstFocusableEl.focus();
        e.preventDefault();
        gradientOverlayInstance.scrollToTop();
    });
}

function trapFocus(element) {
    try {
        const keyDownHandler = (e) => {
            const focusableEls = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
            const firstFocusableEl = focusableEls[0];
            const lastFocusableEl = focusableEls[focusableEls.length - 1];
            const KEYCODE_TAB = 9;

            const isTabPressed = (e.key === "Tab" || e.keyCode === KEYCODE_TAB);
            if (!isTabPressed) { return; }
            if (e.shiftKey) /* shift + tab */ {
                if (lastFocusableEl && document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } else if /* tab */ (lastFocusableEl && document.activeElement === lastFocusableEl) {
                firstFocusableEl.focus();
                e.preventDefault();
            }
        };
        element.addEventListener("keydown", keyDownHandler);
    } catch (err) {
        // no-op
    }
}